 
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyEmployeeTasks } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { WorkReportDialog } from '../../components/WorkReportDialog';
import { TASK_STATUS, formatAmount, formatDate } from '../../lib/workStatus';
import { ChevronRight, Send } from 'lucide-react';
import { toast } from 'sonner';

const TaskProgress = ({ task }) => {
  if (!task.planned_amount) return null;
  const percent = Math.min(100, Math.round((task.reported_amount / task.planned_amount) * 100));
  return (
    <div className="mt-2" data-testid={`employee-task-progress-${task.id}`}>
      <div className="flex justify-between text-xs text-[#2E2E2E]/60 mb-1">
        <span>{formatAmount(task.reported_amount, task.unit)} / {formatAmount(task.planned_amount, task.unit)}</span>
        <span>{percent}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-200">
        <div className="h-2 rounded-full bg-[#1F3D2B] transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const EmployeeTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportTask, setReportTask] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const data = await getMyEmployeeTasks();
      setTasks(data || []);
    } catch (error) {
      toast.error('Neizdevās ielādēt uzdevumus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const visible = tasks.filter((t) => showCompleted || t.status !== 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="employee-tasks-loading">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="employee-tasks-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B]" data-testid="employee-tasks-title">Mani darba uzdevumi</h1>
          <p className="text-[#2E2E2E]/70" data-testid="employee-tasks-subtitle">Atveriet uzdevumu, lai redzētu objektu un iesniegtu paveikto.</p>
        </div>
        <label className="text-xs text-[#2E2E2E]/60 flex items-center gap-2 whitespace-nowrap">
          <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} data-testid="employee-tasks-show-completed" />
          Rādīt pabeigtos
        </label>
      </div>

      {visible.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-10 text-center text-[#2E2E2E]/60" data-testid="employee-tasks-empty">Pašlaik nav piešķirtu uzdevumu.</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map((task) => {
            const status = TASK_STATUS[task.status] || { label: task.status, className: 'bg-gray-100 text-gray-700' };
            return (
              <Card key={task.id} className="border-0 shadow-sm" data-testid={`employee-task-card-${task.id}`}>
                <CardContent className="p-4">
                  <Link to={`/portal/employee/tasks/${task.id}`} className="block" data-testid={`employee-task-detail-${task.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1F3D2B] text-lg leading-tight" data-testid={`employee-task-title-${task.id}`}>{task.title}</p>
                        <p className="text-sm text-[#2E2E2E]/70 truncate" data-testid={`employee-task-project-${task.id}`}>
                          {task.project_name || task.property_name || '-'}{task.cadastral_number ? ` · ${task.cadastral_number}` : ''}
                        </p>
                        <p className="text-xs text-[#2E2E2E]/60 mt-1">
                          {task.work_type_label || 'Darbs'}{task.planned_amount ? ` · ${formatAmount(task.planned_amount, task.unit)}` : ''}{task.due_date ? ` · termiņš ${formatDate(task.due_date)}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={status.className} data-testid={`employee-task-status-${task.id}`}>{status.label}</Badge>
                        <ChevronRight className="w-5 h-5 text-[#2E2E2E]/40" />
                      </div>
                    </div>
                    <TaskProgress task={task} />
                  </Link>
                  {task.status !== 'completed' && (
                    <Button variant="outline" className="mt-3 w-full h-11" onClick={() => setReportTask(task)} data-testid={`employee-task-report-${task.id}`}>
                      <Send className="w-4 h-4 mr-2" /> Iesniegt paveikto
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <WorkReportDialog open={!!reportTask} onOpenChange={(open) => !open && setReportTask(null)} task={reportTask} tasks={tasks} onSubmitted={fetchTasks} />
    </div>
  );
};

export default EmployeeTasks;
